import { useQuery } from "@tanstack/react-query";
import { createFileRoute, redirect } from "@tanstack/react-router";

import { BRAND } from "~/brand";
import { LoadingScreen } from "~/components/LoadingScreen";
import { GiftOptionsPanel } from "~/components/vriendenvoordeel/GiftOptionsPanel";
import { SharePanel } from "~/components/vriendenvoordeel/SharePanel";
import { StepsPanel } from "~/components/vriendenvoordeel/StepsPanel";
import { useFlowStart } from "~/hooks/use-flow-start";
import { currentContractQueryOptions, profileQueryOptions } from "~/lib/api";
import { authClient } from "~/lib/auth";
import { formatEuro } from "~/lib/payout";
import {
  REFERRAL_ENABLED,
  REFERRAL_REWARD_FALLBACK_EUROS,
  referralRewardQueryOptions,
} from "~/lib/viral-loops";

export const Route = createFileRoute("/(authenticated)/_app/vriendenvoordeel/")({
  beforeLoad: () => {
    if (!REFERRAL_ENABLED) throw redirect({ to: "/dashboard" });
  },
  loader: async ({ context }) => {
    await Promise.all([
      context.queryClient.ensureQueryData(profileQueryOptions()),
      context.queryClient.ensureQueryData(currentContractQueryOptions()),
    ]);

    // Not awaited: the amount has a fallback, and ad blockers make this host hang.
    void context.queryClient.prefetchQuery(referralRewardQueryOptions());
  },
  pendingComponent: LoadingScreen,
  component: Vriendenvoordeel,
});

function Vriendenvoordeel() {
  useFlowStart("vriendenvoordeel");
  const { data: session } = authClient.useSession();
  const { data: profileData } = useQuery(profileQueryOptions());
  const { data: contract } = useQuery(currentContractQueryOptions());
  const { data: rewardEuros = REFERRAL_REWARD_FALLBACK_EUROS } = useQuery(
    referralRewardQueryOptions(),
  );
  const reward = formatEuro(rewardEuros);

  return (
    <div className="mx-auto max-w-6xl p-5 pt-10 lg:p-10">
      <div className="mb-8">
        <h1 className="text-3xl font-medium lg:text-4xl">
          Nodig een vriend uit en ontvang allebei {reward}
        </h1>
        <p className="text-muted-foreground mt-1.5 max-w-3xl text-sm lg:text-base">
          Ken je een vriend, buur, collega of familielid met een laadpaal? Nodig hem uit
          voor {BRAND.name}. Zodra hij actief is, ontvangen jullie allebei {reward}. Er
          zit geen limiet op het aantal vrienden dat je kunt uitnodigen.
        </p>
      </div>

      <div className="flex flex-col gap-8">
        <SharePanel
          email={session?.user.email ?? profileData?.profile?.email}
          canRefer={
            contract?.mandate?.active === true && contract.mandate.hasStarted === true
          }
          reward={rewardEuros}
        />
        <StepsPanel reward={rewardEuros} />
        <GiftOptionsPanel reward={rewardEuros} />
      </div>
    </div>
  );
}
